import React, { useEffect, useState } from "react";
import { ClipboardList, Flower2, AlertTriangle, Package, Check, Trash2, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, num } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Badge, Modal } from "@/components/common";

const PRESETS = [["Hari ini", 0], ["7 hari", 6], ["30 hari", 29], ["3 bulan", 89], ["6 bulan", 179], ["1 tahun", 364]];
const isoDate = (d) => d.toISOString().slice(0, 10);

export default function DailyReport() {
  const { branch, isOwner } = useAuth();
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    if (!branch) return;
    setLoading(true);
    try {
      const [t, h] = await Promise.all([
        api.get("/daily-reports/today", { branch_id: branch.id }),
        api.get("/daily-reports", { branch_id: branch.id }),
      ]);
      setToday(t); setHistory(h);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [branch]);

  const del = async (id) => {
    if (!window.confirm("Hapus laporan ini?")) return;
    try { await api.delete(`/daily-reports/${id}`); toast.success("Laporan dihapus"); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (loading) return <Loading label="Memuat laporan..." />;

  return (
    <>
      <PageHeader title="Laporan Harian" subtitle={branch?.name} icon={ClipboardList} />

      <TodayForm key={today?.report?.id || "new"} today={today} branch={branch} onSaved={load} />

      {isOwner && <Productivity branch={branch} />}

      <div className="text-sm font-semibold uppercase text-slate-400 mb-2 mt-6">Riwayat Laporan</div>
      {history.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Belum ada laporan" message="Laporan harian yang dikirim staff akan muncul di sini." />
      ) : (
        <div className="space-y-3">
          {history.map((r) => (
            <Card key={r.id} data-testid={`report-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800">{r.date}</span>
                    <span className="text-sm text-slate-500">{r.user_name}</span>
                    <Badge className="bg-primary/10 text-primary flex items-center gap-1"><Flower2 size={12} /> {r.bouquet_count} rangkaian</Badge>
                    {r.care_done && <Badge className="bg-emerald-100 text-emerald-700">perawatan ✓</Badge>}
                    {r.issues_note && <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1"><AlertTriangle size={12} /> kendala</Badge>}
                  </div>
                  {r.tasks_text && <div className="text-sm text-slate-600 mt-1">{r.tasks_text}</div>}
                  {(r.waste_note || r.materials_note || r.issues_note) && (
                    <div className="text-xs text-slate-400 mt-1 space-x-3">
                      {r.waste_note && <span>Waste: {r.waste_note}</span>}
                      {r.materials_note && <span>Bahan: {r.materials_note}</span>}
                      {r.issues_note && <span>Kendala: {r.issues_note}</span>}
                    </div>
                  )}
                  <div className="text-xs text-slate-400 mt-1">{fmtDateTime(r.created_at)}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="secondary" size="sm" onClick={() => setEditing(r)} data-testid={`report-edit-${r.id}`}>Edit</Button>
                  {isOwner && <button onClick={() => del(r.id)} className="text-xs text-rose-600 inline-flex items-center gap-1" data-testid={`report-del-${r.id}`}><Trash2 size={13} /></button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && <EditModal report={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <span className="text-sm font-medium text-slate-600 mb-1 flex items-center gap-1.5">{Icon && <Icon size={14} />} {label}</span>
    {children}
  </div>
);

function TodayForm({ today, branch, onSaved }) {
  const r = today?.report;
  const [tasks, setTasks] = useState(r?.tasks_text || "");
  const [bouquets, setBouquets] = useState(r?.bouquet_count ?? 0);
  const [careDone, setCareDone] = useState(r?.care_done ?? today?.care_done_today ?? false);
  const [waste, setWaste] = useState(r?.waste_note || "");
  const [materials, setMaterials] = useState(r?.materials_note || "");
  const [issues, setIssues] = useState(r?.issues_note || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/daily-reports", { branch_id: branch.id, tasks_text: tasks, bouquet_count: Number(bouquets) || 0, care_done: careDone, waste_note: waste, materials_note: materials, issues_note: issues });
      toast.success(today?.submitted ? "Laporan diperbarui" : "Laporan terkirim");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Card className="mb-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-slate-800">Laporan Hari Ini · {today?.date}</div>
        {today?.submitted ? <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1"><Check size={12} /> Sudah dikirim</Badge> : <Badge className="bg-slate-100 text-slate-500">Belum dikirim</Badge>}
      </div>
      <Field label="Rincian tugas yang dikerjakan" icon={ClipboardList}>
        <textarea value={tasks} onChange={(e) => setTasks(e.target.value)} rows={2} placeholder="mis. Merangkai buket, bersih kulkas, layani pelanggan..." className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" data-testid="report-tasks" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Jumlah rangkaian buket" icon={Flower2}>
          <input type="number" min="0" value={bouquets} onChange={(e) => setBouquets(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="report-bouquets" />
        </Field>
        <label className="flex items-end gap-2 pb-2 cursor-pointer">
          <input type="checkbox" checked={careDone} onChange={(e) => setCareDone(e.target.checked)} data-testid="report-care-done" />
          <span className="text-sm text-slate-600">Checklist perawatan dikerjakan {today?.care_done_today && "(tercatat ✓)"}</span>
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Bunga rusak (waste)" icon={AlertTriangle}>
          <input value={waste} onChange={(e) => setWaste(e.target.value)} placeholder="mis. 3 tangkai mawar layu" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="report-waste" />
        </Field>
        <Field label="Bahan pendukung terpakai" icon={Package}>
          <input value={materials} onChange={(e) => setMaterials(e.target.value)} placeholder="mis. kertas, pita, kawat" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="report-materials" />
        </Field>
        <Field label="Kendala operasional" icon={AlertTriangle}>
          <input value={issues} onChange={(e) => setIssues(e.target.value)} placeholder="mis. AC bermasalah" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="report-issues" />
        </Field>
      </div>
      <div className="flex justify-end">
        <Button onClick={submit} disabled={busy} data-testid="report-submit">{busy ? "Menyimpan..." : today?.submitted ? "Perbarui Laporan" : "Kirim Laporan"}</Button>
      </div>
    </Card>
  );
}

function Productivity({ branch }) {
  const [from, setFrom] = useState(isoDate(new Date(Date.now() - 29 * 864e5)));
  const [to, setTo] = useState(isoDate(new Date()));
  const [scope, setScope] = useState("branch");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const bid = scope === "branch" ? branch?.id : undefined;

  useEffect(() => {
    setLoading(true);
    api.get("/productivity", { date_from: from, date_to: to, branch_id: bid }).then(setData).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [from, to, bid]);

  const applyPreset = (days) => { setFrom(isoDate(new Date(Date.now() - days * 864e5))); setTo(isoDate(new Date())); };
  const t = data?.totals || {};

  return (
    <Card className="mb-6">
      <div className="flex items-center gap-2 mb-3"><BarChart3 size={18} className="text-primary" /><span className="font-semibold text-slate-800">Rekap Produktivitas Rangkaian</span></div>
      <div className="flex flex-wrap gap-2 mb-3">
        {PRESETS.map(([l, d]) => <button key={l} onClick={() => applyPreset(d)} className="px-3 h-8 rounded-xl text-xs bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid={`prod-preset-${d}`}>{l}</button>)}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 px-3 rounded-xl border border-slate-300 text-sm" data-testid="prod-from" />
        <span className="text-slate-400">s/d</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 px-3 rounded-xl border border-slate-300 text-sm" data-testid="prod-to" />
        <div className="flex gap-1 ml-auto">
          <button onClick={() => setScope("branch")} className={`px-3 h-9 rounded-xl text-sm ${scope === "branch" ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`} data-testid="prod-scope-branch">{branch?.name || "Cabang ini"}</button>
          <button onClick={() => setScope("all")} className={`px-3 h-9 rounded-xl text-sm ${scope === "all" ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`} data-testid="prod-scope-all">Semua cabang</button>
        </div>
      </div>
      {loading ? <Loading /> : !data ? null : data.rows.length === 0 ? (
        <div className="text-sm text-slate-400 py-4 text-center">Belum ada laporan pada periode ini.</div>
      ) : (
        <>
          <div className="flex gap-6 mb-3 text-sm">
            <div><span className="text-slate-400">Total rangkaian: </span><span className="font-bold text-slate-800">{num(t.bouquets ?? 0)}</span></div>
            <div><span className="text-slate-400">Staff: </span><span className="font-bold text-slate-800">{t.staff ?? 0}</span></div>
            <div><span className="text-slate-400">Laporan: </span><span className="font-bold text-slate-800">{t.reports ?? 0}</span></div>
          </div>
          <div className="space-y-2">
            {data.rows.map((r, i) => (
              <div key={r.user_id} className="flex items-center gap-3" data-testid={`prod-row-${r.user_id}`}>
                <span className="w-6 text-center text-sm font-semibold text-slate-400">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 text-sm">{r.name}</div>
                  <div className="text-xs text-slate-400">{r.role} · {r.days} hari lapor · {r.avg_bouquets}/hari{r.issues ? ` · ${r.issues} kendala` : ""}</div>
                </div>
                <div className="w-32 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, (r.bouquets / (data.rows[0].bouquets || 1)) * 100)}%` }} />
                </div>
                <span className="w-16 text-right font-bold text-slate-800">{r.bouquets}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function EditModal({ report, onClose, onSaved }) {
  const [tasks, setTasks] = useState(report.tasks_text || "");
  const [bouquets, setBouquets] = useState(report.bouquet_count ?? 0);
  const [careDone, setCareDone] = useState(report.care_done ?? false);
  const [waste, setWaste] = useState(report.waste_note || "");
  const [materials, setMaterials] = useState(report.materials_note || "");
  const [issues, setIssues] = useState(report.issues_note || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/daily-reports/${report.id}`, { tasks_text: tasks, bouquet_count: Number(bouquets) || 0, care_done: careDone, waste_note: waste, materials_note: materials, issues_note: issues });
      toast.success("Laporan diperbarui");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Edit Laporan · ${report.date} · ${report.user_name}`}>
      <div className="space-y-4">
        <textarea value={tasks} onChange={(e) => setTasks(e.target.value)} rows={2} placeholder="Rincian tugas" className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" data-testid="report-edit-tasks" />
        <div className="grid grid-cols-2 gap-3">
          <input type="number" min="0" value={bouquets} onChange={(e) => setBouquets(e.target.value)} placeholder="Jumlah rangkaian" className="h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="report-edit-bouquets" />
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={careDone} onChange={(e) => setCareDone(e.target.checked)} /> Perawatan dikerjakan</label>
        </div>
        <input value={waste} onChange={(e) => setWaste(e.target.value)} placeholder="Bunga rusak" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" />
        <input value={materials} onChange={(e) => setMaterials(e.target.value)} placeholder="Bahan pendukung" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" />
        <input value={issues} onChange={(e) => setIssues(e.target.value)} placeholder="Kendala" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={busy} data-testid="report-edit-save">{busy ? "..." : "Simpan"}</Button>
        </div>
      </div>
    </Modal>
  );
}
